// Where the STAFF app lives, resolved from the browser rather than hardcoded.
//
// Same rule the API client uses (features/api/consumerClient.js): the tenant is
// the hostname, so whatever subdomain you opened is the one you stay on. A
// literal `test1.localhost:3000` would work on one developer's machine and send
// every other tenant to the wrong desk.
//
// In DEV the two React apps sit on different ports of the same tenant host —
// this B2C app on :3001, the staff app on :3000. Anywhere else the staff app is
// served from this same origin under /app, so a bare path is already correct.

const B2C_DEV_PORT = '3001';
const STAFF_DEV_PORT = '3000';

// The bare domain is the marketing site, NOT a tenant. Staying on it would send
// a visitor to interestng.com/app/login, where tenancy resolves to nothing —
// so the one case that cannot use "stay where you are" gets a named tenant.
//
// An explicit list, not "does the hostname have a subdomain?": `www` has one and
// is still central, and UAT hosts must keep resolving to themselves. Anything
// not listed here is treated as a tenant and left alone, which is the safe
// default — a missing entry sends someone to a login page, while a wrong
// "central" guess would drag a real tenant off their own domain.
const CENTRAL_HOSTS = ['interestng.com', 'www.interestng.com'];
const DEFAULT_TENANT = 'app.interestng.com';

/**
 * @param {string} path  a path inside the staff app, e.g. '/app/login'
 */
export default function staffAppUrl(path = '/app/login') {
  if (typeof window === 'undefined') return path;
  const { protocol, hostname, port } = window.location;
  if (port === B2C_DEV_PORT) return `${protocol}//${hostname}:${STAFF_DEV_PORT}${path}`;
  if (CENTRAL_HOSTS.includes(hostname)) return `${protocol}//${DEFAULT_TENANT}${path}`;
  return path;
}

/**
 * Where a DISTRIBUTOR signs in — the live staff app's login, one fixed address
 * on every host, local dev included (2026-09-26, on request: "for distributors
 * login use this https://app.interestng.com/app/login"). Deliberately NOT
 * staffAppUrl(): locally that gives http://127.0.0.1:3000/app/login, which only
 * opens when the staff app is running on this machine — and it usually is not
 * ("Safari can't connect to the server 127.0.0.1").
 * The header's Login and the Distributor plan buttons all use this one
 * constant, so they can never point at two different places.
 */
export const DISTRIBUTOR_LOGIN_URL = 'https://app.interestng.com/app/login';
